import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {CREATE_BOND} from 'graphql/mutations/assetManagement';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {handleClearBondsState} from 'redux/store/assetClass';
import {currencySymbols} from 'utils/mock';

/**
 * Turns a GraphQL error list into something an operator can act on.
 *
 * A rejected create comes back with one entry per missing field, each a long
 * "Variable \"$input\" got invalid value {...}; Field \"name\" of required
 * type \"String!\" was not provided." The useful part is the field name, so
 * the noise is stripped and the fields are named together.
 */
const describeErrors = errors => {
  if (!errors?.length) return '';
  const missing = [];
  errors.forEach(error => {
    const match = /Field "([^"]+)" of required type/.exec(error?.message ?? '');
    if (match && missing.indexOf(match[1]) === -1) missing.push(match[1]);
  });
  if (!missing.length) return errors[0]?.message ?? '';
  return `This bond is missing required details: ${missing.join(
    ', ',
  )}. Complete the earlier steps before creating it.`;
};

export const CreateBondFinishModal = ({handleClose}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bondsData = useSelector(state => state?.assetClass);
  const {assetId: assetKey} = useParams();

  const [createBond, {loading}] = useMutation(CREATE_BOND);

  /**
   * The instrument half of the wizard, as the gateway requires it.
   *
   * Steps 5 and 6 are reachable directly from the sidebar, so an operator can
   * land on the final Create with steps 1–4 untouched. The app already knows
   * this — the shell renders "6 of 16 required fields saved" — it just never
   * acted on it, and the only thing stopping a malformed create was the
   * gateway rejecting the variables. Checking here turns a round trip and a
   * red toast into an answer the operator gets before they press the button.
   */
  const BASE_REQUIREMENTS = [
    {field: 'name', label: 'Bond name', step: 'General details'},
    {field: 'language', label: 'Language', step: 'General details'},
    {field: 'listingType', label: 'Listing type', step: 'General details'},
    {field: 'issuerType', label: 'Issuer type', step: 'General details'},
    {
      field: 'shariaCompliant',
      label: 'Sharia compliant',
      step: 'Trading settings',
    },
  ];

  const missingBase = BASE_REQUIREMENTS.filter(entry => {
    // The draft is split in two: `bonds` holds the step-two terms (ticker,
    // coupon, maturity) and `bondInstruments` holds the instrument itself.
    // These five are instrument fields, so they live in the latter — reading
    // `bonds` reported every one of them missing on a fully completed wizard.
    const value = bondsData?.bondInstruments?.[entry.field];
    // `false` is a legitimate answer for a boolean; only absence counts.
    return value === undefined || value === null || value === '';
  });

  const handleCreateBond = () => {
    if (missingBase.length) {
      // The ESLint env here has no es6 globals, so `Set` reads as undefined.
      const steps = missingBase
        .map(entry => entry.step)
        .filter((step, index, all) => all.indexOf(step) === index);
      toast.error(
        `Missing ${missingBase
          .map(entry => entry.label)
          .join(', ')} — complete ${steps.join(' and ')} first.`,
      );
      return;
    }

    // Field set matches BondInput / BaseFinancialInstrumentInput exactly.
    // Everything the FinancialInstrument migration removed — dabaScore,
    // pitchDeck, faq, missionAndVision, solutions, liquidity, investmentRisk,
    // keyStatistics, CUSIP, marketCapitalization, riskAndEsg, impactAndEsg,
    // additionalDisclosures, additionalInvestmentTerms, legalChallenges,
    // projectFinancingDetails, outlookAsOfDate, creditWatchAsOfDate — is gone
    // from this payload. The flat primaryBroker* fields and useOfProceeds now
    // travel inside `offering`.
    const {
      description,
      language,
      topics,
      documents,
      tradeable,
      feeSchedule,
      issuer,
      useInventoryForTrade,
      issuerType,
      currency,
      name,
      icon,
      endDate,
      groupId,
      sectorIndustry,
      exchange,
      invitedUsers,
      invitedGroups,
      listingType,
      shariaCompliant,
      forDabaPro,
      enabledForFreeSimulation,
      fractionalShares,
      allocatedUnits,
      minTradeableUnits,
      maxTradeableUnits,
      lotSize,
      startDate,
      orderExpirationLimit,
      marketPhase,
      riskLevel,
      countries,
      keyFacts,
      disclosures,
      offering,
      settings,
    } = bondsData?.bondInstruments;
    const {
      // ticker and isin are entered on the step-two Overview screen, so they
      // live in the `bonds` slice — but they belong to the base input.
      ticker,
      isin,
      type,
      tenor,
      nextCallPrice,
      couponRate,
      couponFrequency,
      callable,
      yieldToMaturity,
      issuanceVolume,
      issueSizeUnits,
      currentMarketPrice,
      maturityDate,
      managementFee,
      gracePeriodPrincipal,
      gracePeriodInterest,
      nextCallDate,
      nextCouponDate,
      issuanceDate,
      rating,
      faceValue,
      creditOutlook,
      amountOutstanding,
      realizedYield,
      schedule,
    } = bondsData?.bonds;

    createBond({
      variables: {
        input: {
          type,
          tenor,
          couponRate,
          couponFrequency,
          callable: callable ?? false,
          yieldToMaturity,
          issuanceVolume,
          issueSizeUnits,
          currentMarketPrice,
          maturityDate,
          managementFee,
          gracePeriodPrincipal,
          gracePeriodInterest,
          nextCallPrice,
          rating,
          faceValue,
          issuerType,
          creditOutlook,
          amountOutstanding,
          realizedYield,
          ...(schedule?.length && {schedule}),
          ...(nextCallDate && {nextCallDate: moment(nextCallDate).format()}),
          ...(nextCouponDate && {
            nextCouponDate: moment(nextCouponDate).format(),
          }),
          ...(issuanceDate && {issuanceDate: moment(issuanceDate).format()}),
          baseFinancialInstrumentInput: {
            key: 'BOND',
            assetClassKey: assetKey,
            name,
            description,
            icon,
            language,
            ticker,
            isin,
            topics,
            countries,
            keyFacts,
            disclosures,
            documents,
            settings,
            groupId,
            sectorIndustry,
            exchange,
            invitedUsers,
            invitedGroups,
            listingType,
            marketPhase,
            riskLevel,
            tradeable,
            useInventoryForTrade,
            shariaCompliant,
            forDabaPro,
            enabledForFreeSimulation,
            fractionalShares,
            lotSize,
            allocatedUnits,
            minTradeableUnits,
            maxTradeableUnits,
            orderExpirationLimit,
            ...(offering && {offering}),
            ...(startDate && {startDate: moment(startDate).format()}),
            ...(endDate && {endDate: moment(endDate).format()}),
            issuer: {
              id: issuer,
              issuerType,
            },
            currency: {
              name: currencySymbols[currency]?.name,
              symbol: currencySymbols[currency]?.symbol,
              currencyCode: currencySymbols[currency]?.value,
            },
            // An empty fee schedule must be omitted, not sent as `false`.
            ...(Array.isArray(feeSchedule) &&
              feeSchedule.length && {
                feeSchedule: feeSchedule.map(data => ({
                  feeName: data?.feeName,
                  feeType: data?.feeType,
                  ...(data?.fixedFeeAmount && {
                    fixedFeeAmount: Number(data?.fixedFeeAmount),
                  }),
                  ...(data?.feeType === 'ENTRY' && {
                    level: data?.level,
                    // Sent explicitly, not spread conditionally: the whole schedule is
                    // overwritten on save, so an omitted false reads as "unchanged"
                    // against a previously-true value and the toggle could never be
                    // turned back off.
                    perExchange: Boolean(data?.perExchange),
                  }),
                  ...(data?.variableFeeAmount && {
                    variableFeeAmount: Number(data?.variableFeeAmount),
                  }),
                  ...(data?.scalable != undefined && {
                    scalable: data?.scalable,
                  }),
                  ...(data?.feeType === 'RECURRING' && {
                    frequency: data?.frequency,
                    origination: data?.origination,
                  }),
                  ...(data?.feeType === 'EXIT' &&
                    Array.isArray(data?.vector) && {
                      vector: data?.vector?.map(vect => ({
                        duration: vect?.duration,
                        magnitude: vect?.magnitude,
                      })),
                    }),
                })),
              }),
          },
        },
      },
    })
      .then(({data, errors}) => {
        // `data` is null when the gateway rejects the variables outright, so
        // destructuring `createBond` in the parameter list threw a TypeError
        // and sent even a well-described validation failure down the catch.
        const result = data?.createBond;
        if (errors?.length) {
          toast.error(describeErrors(errors));
        } else if (result?.__typename === 'Error') {
          toast.error(result.message);
        } else if (!result) {
          toast.error('The bond was not created. Please try again.');
        } else {
          toast.success('Created Successfully');
          dispatch(handleClearBondsState());
          navigate(`/dashboard/asset-management/class/${assetKey}/instruments`);
        }
      })
      .catch(error => {
        // `toast.error(message, error)` put the explanation in the options
        // slot, where it was silently dropped — so a create that failed on
        // five named missing fields surfaced only as "Something went wrong".
        //
        // A variable-validation failure is a 400, which Apollo classes as a
        // network error: the per-field list is under `networkError.result`,
        // not `graphQLErrors`, and reading only the latter left the operator
        // with "Received status code 400".
        toast.error(
          describeErrors(error?.graphQLErrors) ||
            describeErrors(error?.networkError?.result?.errors) ||
            error.message,
        );
      });
  };

  return (
    <div className="confirm-modal">
      <div>
        <h1>Create Bond</h1>
        <p>
          Please make sure the details are correctly inputted before proceeding
        </p>
      </div>
      <div className="buttons">
        <Button
          disabled={loading}
          label={!loading ? 'Create' : 'Loading...'}
          onClick={handleCreateBond}
        />
        <Button type="secondary" label="No" onClick={handleClose} />
      </div>
    </div>
  );
};
